import * as React from 'react';
import cx from 'classnames';

import Button from 'components/ButtonDeprecated';
import Icon, { IconName } from 'components/Icon';
import LayerTrigger from 'components/LayerTrigger';
import Tooltip from 'components/Tooltip';

import './styles/TooltipHovercardIconButton.scss';

// In order to control the click layer programmatically (eg, to close it when a button is clicked),
// we have to access the |showLayer| and/or |hideLayer| methods on the LayerTrigger ref from the
// code that should be able to control the layer. We do this by conditionally accepting a function
// as our |layer| prop if the |controlled| prop is true, into which we pass the LayerTrigger ref.
// The discriminated union type LayerProps ensures that Typescript understands this arrangement.
type ControlledLayerProps = {
  controlled: true;
  layer: (x: any) => React.ReactElement;
};
type UncontrolledLayerProps = {
  controlled?: false;
  layer: React.ReactElement;
};
type LayerProps = ControlledLayerProps | UncontrolledLayerProps;

export type Props = {
  layerAlignment?: 'top' | 'bottom' | 'left' | 'right';
  layerPlacement?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  controlled?: boolean;
  'data-test'?: string;
  'data-test-selector'?: string;
  disabled?: boolean;
  eventKey?: string;
  eventProperties?: { [key: string]: any };
  icon: IconName;
  onHide?: Function;
  onShow?: Function;
  switchedOn?: boolean;
  tooltipAlignment?: 'top' | 'bottom' | 'left' | 'right';
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  tooltipText: string;
} & LayerProps;

export type State = {
  hovercardOpen: boolean;
};

/* An icon button that shows a tooltip on hover and toggles a hovercard layer on click.
 * When |disabled|, it still shows the tooltip, but doesn't open the hovercard.
 * The |switchedOn| flag styles the button in brand colors instead of greys, and is meant
 * to indicate that some effect of the button is currently active.
 *
 * Currently only supports a single button type, 32px square with an 18px icon. Supporting
 * arbitrary buttons is possible but tricky to achieve with nested LayerTriggers due to the
 * following:
 * 1. layer positioning seems to break on rerender unless the manually-controlled tooltip
 *    LayerTrigger is nested inside the button (probably something to do with the ref)
 * 2. when the tooltip LayerTrigger is nested inside the button, the tooltip layer is positioned
 *    according to the button's content rather than the button itself, which looks bad for any
 *    button with padding.
 */
class TooltipHovercardIconButton extends React.Component<Props, State> {
  static defaultProps = { layerPlacement: 'bottom', tooltipPlacement: 'top' };

  tooltipLayerTriggerRef: React.RefObject<LayerTrigger>;
  cardLayerTriggerRef: React.RefObject<LayerTrigger>;

  constructor(props: Props) {
    super(props);
    this.state = { hovercardOpen: false };
    this.tooltipLayerTriggerRef = React.createRef<LayerTrigger>();
    this.cardLayerTriggerRef = React.createRef<LayerTrigger>();
  }

  handleButtonEnter = () => {
    if (!this.state.hovercardOpen) {
      this.tooltipLayerTriggerRef.current?.showLayer();
    }
  };

  handleButtonLeave = (event: React.MouseEvent) => {
    const button = event.currentTarget;
    if (button instanceof HTMLElement) {
      button.blur();
    }
    this.tooltipLayerTriggerRef.current?.hideLayer();
  };

  handleHovercardShow = () => {
    this.setState({ hovercardOpen: true });
    this.props.onShow && this.props.onShow();
  };

  handleHovercardHide = () => {
    this.setState({ hovercardOpen: false });
    this.props.onHide && this.props.onHide();
  };

  renderLayer = (): React.ReactElement => {
    const layerProps = this.props;
    return layerProps.controlled
      ? layerProps.layer(this.cardLayerTriggerRef.current)
      : layerProps.layer;
  };

  /**
   * There is a LOT of weirdness trying to get multiple <LayerTrigger>s to work together, but
   * that's what we needed here in order to show both a tooltip and hovercard.
   *
   * The hovercard layer is triggered conventionally by click, but the tooltip layer is triggered
   * manually via the |handleButtonEnter| and |handleButtonLeave| functions. These use the
   * |tooltipLayerTriggerRef| to manage the showing and hiding of the tooltip layer.
   *
   * The |hovercardOpen| state is managed by |handleHovercardShow| and |handleHovercardHide|,
   * and allows us to maintain a gray background on the button when the hovercard is open.
   *
   * */
  renderButtonWithTooltip = () => {
    const {
      className,
      eventKey,
      eventProperties,
      icon,
      layerAlignment,
      layerPlacement,
      switchedOn,
      tooltipText,
      tooltipAlignment,
      tooltipPlacement,
    } = this.props;
    const { hovercardOpen } = this.state;

    return (
      <LayerTrigger
        trigger={['click', 'manual']}
        ref={this.cardLayerTriggerRef}
        alignment={layerAlignment}
        placement={layerPlacement}
        layer={this.renderLayer()}
        onShow={this.handleHovercardShow}
        onHide={this.handleHovercardHide}>
        <Button
          className={cx(
            className,
            'TooltipHovercardIconButton',
            switchedOn && 'TooltipHovercardIconButton-buttonSwitchedOn',
            !switchedOn && 'TooltipHovercardIconButton-hovercardSwitchedOff',
            hovercardOpen && 'TooltipHovercardIconButton-hovercardOpen'
          )}
          level="icon"
          onMouseEnter={this.handleButtonEnter}
          onMouseLeave={this.handleButtonLeave}
          eventKey={eventKey}
          eventProperties={eventProperties}
          data-test={`${this.props['data-test']}-button`}
          data-test-selector={this.props['data-test-selector']}>
          <LayerTrigger
            ref={this.tooltipLayerTriggerRef}
            trigger="manual"
            alignment={tooltipAlignment}
            placement={tooltipPlacement}
            layer={
              <Tooltip data-test="tooltip" className="TooltipHovercardIconButton-tooltip">
                {tooltipText}
              </Tooltip>
            }>
            <div className="TooltipHovercardIconButton-iconWrapper">
              <Icon name={icon} color="chalkboard" className="TooltipHovercardIconButton-icon" />
            </div>
          </LayerTrigger>
        </Button>
      </LayerTrigger>
    );
  };

  render() {
    const {
      className,
      disabled,
      icon,
      tooltipAlignment,
      tooltipPlacement,
      tooltipText,
    } = this.props;

    return disabled ? (
      <Button
        data-test={`${this.props['data-test']}-button`}
        className={cx(
          className,
          'TooltipHovercardIconButton',
          'TooltipHovercardIconButton-disabled'
        )}
        level="icon">
        <LayerTrigger
          trigger="hover"
          alignment={tooltipAlignment}
          placement={tooltipPlacement}
          layer={
            <Tooltip data-test="disabled-tooltip" className="TooltipHovercardIconButton-tooltip">
              {tooltipText}
            </Tooltip>
          }>
          <div
            className="TooltipHovercardIconButton-iconWrapper"
            data-test={`${this.props['data-test']}-disabled-layer-trigger`}>
            <Icon name={icon} color="chalkboard" className="TooltipHovercardIconButton-icon" />
          </div>
        </LayerTrigger>
      </Button>
    ) : (
      this.renderButtonWithTooltip()
    );
  }
}

export default TooltipHovercardIconButton;
